import { artists, contacts } from '../../../services/api.admin';
import { fetchTableData, deleteTableData } from '../tables';
import { v4 as uuidv4 } from 'uuid';
import { MODELS, TEMP_ID_PREFIX } from '../../../constants/admin/settings';
import { apiDataToState, stateToApiData, isArtistRole } from '../../../selectors/admin/models/artists';
import { stateToApiData as userStateToApiData } from '../../../selectors/admin/models/contacts';
import { fetchDependencies, fetchModel, submit, onInput } from '../forms';
import { apiActionCreator, apiMultipleCalls } from '../../helpers';
import { UNPROCESSABLE_ENTITY_CODE } from '../../../constants/http.codes';
import { ADMIN_FORMS } from '../../../constants/actions';
import { lineupAdd } from './lineup';
import { formDataSelector } from '../../../selectors/admin/forms';
import { filter, find } from 'lodash';
import { contextSubjectSelector } from '../../../selectors/admin/context';

export function fetchList(contextId, pagination) {
    return fetchTableData(
        contextId ? () => artists.getOrg(contextId, pagination) : () => artists.get(pagination),
        'artists',
        {
            pagination
        }
    );
}

export function fetchArtistContactDependencies(organizationId) {
    return fetchDependencies([() => contacts.getOrgAll(organizationId)], {
        append: true
    });
}

export function deleteArtists() {
    return deleteTableData(artists.delete, MODELS.ARTIST);
}

export function create(data) {
    return submit(() => artists.create(stateToApiData(data)), 'Artist successfully created.');
}

export function update(data, id, _, onSuccess) {
    return (dispatch, getState) => {
        const context = contextSubjectSelector(getState());
        return submit(() => artists.update(id, stateToApiData(data), context), 'Artist successfully updated.', {
            beforeDone: (_r, _d, _gs, doneFunction) => {
                onSuccess();
                doneFunction();
            }
        })(dispatch, getState);
    };
}

export function fetchMember(id) {
    return (dispatch, getState) => {
        const context = contextSubjectSelector(getState());
        return fetchModel(() => artists.getMember(id, context), MODELS.ARTIST, {
            transform: apiDataToState
        })(dispatch);
    };
}

export function fetchArtistUsers(subContextId, pagination, contextId, afterDone = () => {}, all = false) {
    return fetchTableData(
        contextId
            ? () => artists[all ? 'getAllOrgUsers' : 'getOrgUsers'](subContextId, contextId, pagination)
            : () => artists[all ? 'getAllUsers' : 'getUsers'](subContextId, pagination),
        'users',
        {
            pagination,
            afterDone
        }
    );
}

export function createArtistWithContacts(data, contacts, doneFunction, errorFunction) {
    return (dispatch, getState) => {
        const event = formDataSelector(getState(), { model: MODELS.EVENT });
        if (data.id) {
            apiActionCreator(
                dispatch,
                () => artists.getMember(data.id),
                resp => {
                    const artistResponse = apiDataToState(resp);
                    return apiActionCreator(
                        dispatch,
                        () =>
                            artists.update(
                                data.id,
                                stateToApiData({
                                    ...artistResponse,
                                    organizationIds: [...artistResponse.organizationIds, event.organizationId],
                                    eventIds: [...artistResponse.eventIds, event.id]
                                })
                            ),
                        () => {
                            _createArtistContacts(data.id, contacts, event, doneFunction, dispatch, getState);
                        }
                    );
                }
            );
        } else {
            apiActionCreator(
                dispatch,
                () =>
                    artists.create(
                        stateToApiData({ ...data, organizationIds: [event.organizationId], eventIds: [event.id] }, true)
                    ),
                resp => {
                    dispatch(lineupAdd('artists', { id: resp.id, name: data.name }));
                    dispatch(onInput(MODELS.ENTRY, 'artistId', resp.id));
                    _createArtistContacts(resp.id, contacts, event, doneFunction, dispatch, getState);
                },
                {
                    [UNPROCESSABLE_ENTITY_CODE]: () => {
                        dispatch({
                            type: ADMIN_FORMS.FAILED,
                            errors: { name: 'Artist with this name already exists!' }
                        });
                        errorFunction();
                    }
                }
            );
        }
    };
}

const _newRoleObject = (c, event, artistId) => ({
    artistId,
    function: c.type,
    id: uuidv4(),
    name: c.permission,
    orgId: event.organizationId,
    eventId: event.id,
    accessToClearanceContracts: true,
    accessToPerformanceContracts: c.accessToPerformanceContracts
});

function _createArtistContacts(artistId, users, event, doneFunction, dispatch, getState) {
    const contactsToCreate = filter(users, c => c.id.includes(TEMP_ID_PREFIX));
    const contactsToAddPermissions = filter(users, c => !c.id.includes(TEMP_ID_PREFIX));
    return apiMultipleCalls(
        dispatch,
        contactsToCreate.map(c => () =>
            contacts.create(
                userStateToApiData({
                    email: c.email,
                    fullName: c.fullName,
                    roles: [_newRoleObject(c, event, artistId)]
                })
            )
        ),
        () => {
            return apiMultipleCalls(
                dispatch,
                contactsToAddPermissions.map(c => () =>
                    contacts.update(
                        c.id,
                        userStateToApiData({
                            ...c,
                            roles: [
                                ...c.roles,
                                ...(find(c.roles, r => isArtistRole(r, artistId, event.organizationId, event.id))
                                    ? []
                                    : [_newRoleObject(c, event, artistId)])
                            ].map(r =>
                                isArtistRole(r, artistId, event.organizationId, event.id)
                                    ? { ...r, accessToPerformanceContracts: c.accessToPerformanceContracts }
                                    : { ...r }
                            )
                        }),
                        event.Organization.id
                    )
                ),
                doneFunction
            );
        }
    );
}



// WEBPACK FOOTER //
// ./src/actions/admin/models/artists.js